import React, {FC, useEffect, useMemo, useState} from "react";
import GatewayService from "../../service/GatewayService";
import {DropDownCompString, getKeyVal} from "../util/DropDownComp";
import {useClients} from "../../context/ClientsContext";
import {toast} from "react-hot-toast";
import {TimeUtil} from "../../util/TimeUtil";
import Calendar from "react-calendar";
import '../Calendar.css';
import {ContractTrade, McxDevolvementResult} from "../../models/McxDevolvement";
import {ContractPosition} from "../../models/ContractPosition";
import AppConstants from "../../util/AppConstants";

const SectionTrades: FC<{title:string, rows:ContractTrade[]}> = ({title, rows}) => {
    if (!rows || rows.length === 0) return <h4>No {title}</h4>;

    const totalQty = rows.reduce((acc, row) => acc + row.quantity, 0);

    return <div className="control-group">
        <h3>{title} ({rows.length})</h3>
        <table className="margins-table">
            <thead>
            <tr>
                <th>Time</th>
                <th>Symbol</th>
                <th>Strike</th>
                <th>Type</th>
                <th>Qty</th>
                <th>Avg Price</th>
            </tr>
            </thead>
            <tbody>
            {rows.map((r, idx)=>
                <tr key={idx}>
                    <td>{new Date(r.time).toLocaleString()}</td>
                    <td>{r.tradingsymbol}</td>
                    <td>{r.strike}</td>
                    <td>{r.instrumentType}</td>
                    <td>{r.quantity}</td>
                    <td>{r.average_price.toFixed(2)}</td>
                </tr>
            )}
            </tbody>
            <tfoot>
            <tr>
                <th colSpan={4} style={{textAlign: 'right'}}>Total</th>
                <th>{totalQty}</th>
                <th></th>
            </tr>
            </tfoot>
        </table>
    </div>
}

const SectionPositions: FC<{title:string, rows:ContractPosition[]}> = ({title, rows}) => {
    if (!rows || rows.length === 0) return <h4>No {title}</h4>;

    const totalQty = rows.reduce((acc, row) => acc + row.quantity, 0);

    return <div className="control-group">
        <h3>{title} ({rows.length})</h3>
        <table className="margins-table">
            <thead>
            <tr>
                <th>Symbol</th>
                <th>strike</th>
                <th>type</th>
                <th>Qty</th>
            </tr>
            </thead>
            <tbody>
            {rows.map((r: ContractPosition, idx: number)=>
                <tr key={idx}>
                    <td>{r.tradingsymbol}</td>
                    <td>{r.strike}</td>
                    <td>{r.instrumentType}</td>
                    <td>{r.quantity}</td>
                </tr>
            )}
            </tbody>
            <tfoot>
            <tr>
                <th colSpan={3} style={{textAlign: 'right'}}>Total</th>
                <th>{totalQty}</th>
            </tr>
            </tfoot>
        </table>
    </div>
}


export const McxDevolvementComponent: FC = () => {
    const { clients } = useClients();
    const [clientId, setClientId] = useState<string>("");

    const todayStr = TimeUtil.toUIDate(new Date());
    const [date, setDate] = useState(todayStr);

    const [data, setData] = useState<McxDevolvementResult|null>(null);
    const [loading, setLoading] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const [futureFilter, setFutureFilter] = useState('');
    const [filterZeroQty, setFilterZeroQty] = useState(true);

    const handleDateChange = (newDate: Date | null | [Date | null, Date | null]) => {
        if (newDate instanceof Date) {
            setDate(TimeUtil.toUIDate(newDate));
            setShowCalendar(false);
        }
    };

    const fetchData = () => {
        if (!clientId) return;
        const apiDate = date;
        if (!/^\d{2}-\d{2}-\d{4}$/.test(apiDate)) {
            toast.error("Invalid date format. Please use DD-MM-YYYY");
            return;
        }
        setLoading(true);
        GatewayService.getMcxDevolvement(clientId, apiDate)
            .then(setData)
            .catch(err => {
                console.error(err);
                setData(null);
                toast.error("Failed to fetch MCX devolvement data.");
            })
            .finally(()=>setLoading(false));
    };

    const updateClosings = () => {
        setLoading(true);
        GatewayService.updateClosings()
            .then(resp => {
                toast.success(`Updated ${resp.ok} closings`);
            })
            .catch(err => {
                toast.error("Failed to update closings");
                console.error(err);
            })
            .finally(() => setLoading(false));
    };

    const changeDay = (offset: number) => {
        const currentDate = TimeUtil.parseUIDate(date);
        if (currentDate) {
            currentDate.setDate(currentDate.getDate() + offset);
            setDate(TimeUtil.toUIDate(currentDate));
        }
    };

    const setYesterday = () => {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = TimeUtil.toUIDate(yesterday);
        setDate(yesterdayStr);
    }

    useEffect(()=>{
        if (!clientId && clients && clients.length>0) setClientId(clients[0]);
    }, [clients]);

    useEffect(()=>{
        if (!clientId) return;
        fetchData();
    }, [clientId, date]);

    const filteredData : McxDevolvementResult|null = useMemo(() => {
        if (!data) return null;

        if (!futureFilter && !filterZeroQty) {
            return data;
        }

        const myTrades = data.myTrades
            .filter(t => !futureFilter || (t.tradingsymbol === futureFilter || t.correspondingFuture === futureFilter))
            .filter(t => !filterZeroQty || t.quantity !== 0);

        const exchangeTrades = data.exchangeTrades
            .filter(t => !futureFilter || (t.tradingsymbol === futureFilter || t.correspondingFuture === futureFilter))
            .filter(t => !filterZeroQty || t.quantity !== 0);

        const netPosition = data.netPosition
            .filter((p: any) => !futureFilter || (p.tradingsymbol === futureFilter || p.correspondingFuture === futureFilter))
            .filter(p => !filterZeroQty || p.quantity !== 0);

        const carryForwardPositions = data.carryForwardPositions
            .filter((p: any) => !futureFilter || (p.tradingsymbol === futureFilter || p.correspondingFuture === futureFilter))
            .filter(p => !filterZeroQty || p.quantity !== 0);

        return {
            ...data,
            myTrades,
            exchangeTrades,
            netPosition,
            carryForwardPositions,
        };
    }, [data, futureFilter, filterZeroQty]);

    const futureSymbolsForDropDown = useMemo(() => {
        if (!data) return [];
        const symbols = new Set<string>();
        data.exchangeTrades.forEach(t => {
            if (t.instrumentType === AppConstants.FUT) {
                symbols.add(t.tradingsymbol);
            }
        });
        return Array.from(symbols).sort();
    }, [data]);

    return <div className="container">
        <h2>MCX Devolvement Analysis</h2>
        <div className="positions-header">
            <div className="positions-controls">
                <div className="control-group">
                    <h3>Client</h3>
                    <DropDownCompString text={"Client"} actual={clientId} arr={getKeyVal(clients||[])} set={setClientId}/>
                </div>
                <div className="control-group">
                    <h3>Date</h3>
                    <input type="text" value={date} onChange={e => setDate(e.target.value)} placeholder="DD-MM-YYYY"/>
                    <div style={{display: 'inline-flex'}}>
                        <button onClick={() => changeDay(-1)}>➖</button>
                        <button onClick={setYesterday}>Yesterday</button>
                        <button onClick={() => changeDay(1)}>➕</button>
                    </div>
                    <button onClick={() => setShowCalendar(s => !s)}>{showCalendar ? 'Hide' : 'Show'} Calendar</button>
                    {showCalendar && <Calendar
                        onChange={handleDateChange}
                        value={TimeUtil.parseUIDate(date) || new Date()}
                    />}
                </div>
                <div className="control-group">
                    <h3>Filter</h3>
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        <DropDownCompString text={"Future"} actual={futureFilter} arr={getKeyVal(futureSymbolsForDropDown)} set={setFutureFilter}/>
                        {futureFilter && <span title="Copy future symbol" style={{cursor: 'pointer', marginLeft: '8px'}} onClick={() => {
                            navigator.clipboard.writeText(futureFilter);
                            toast.success('Copied future symbol!');
                        }}>📋</span>}
                    </div>
                    <div>
                        <input type="checkbox" id="filterZeroQty" checked={filterZeroQty} onChange={e => setFilterZeroQty(e.target.checked)}/>
                        <label htmlFor="filterZeroQty" style={{cursor: 'pointer', marginLeft: '4px'}}>Hide Zero Qty</label>
                    </div>
                </div>
                <div className="control-group">
                    <h3>Actions</h3>
                    <button onClick={fetchData} disabled={loading}>{loading ? "Refreshing..." : "Refresh"}</button>
                    <button onClick={updateClosings} disabled={loading}>Update Closings</button>
                </div>
            </div>
            <div className="expiry-filters">
                <h3>Results</h3>
                {loading && <div>Loading...</div>}
                {!loading && !filteredData && <div>No data.</div>}
                {!loading && filteredData && (
                    <div className="positions-list" style={{marginTop: 8}}>
                        <SectionPositions title="Positions at end of the day" rows={filteredData.netPosition}/>
                        <SectionPositions title="Positions carried forward from prev day" rows={filteredData.carryForwardPositions}/>
                        <SectionTrades title="Trades executed by me" rows={filteredData.myTrades}/>
                        <SectionTrades title="Trades executed by exchange" rows={filteredData.exchangeTrades}/>
                    </div>
                )}
            </div>
        </div>
    </div>
}

export default McxDevolvementComponent;
